import { TextArea, Button, Loader, Dimmer } from 'semantic-ui-react';
import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import academyService from 'src/services/api/academyService';
import Toast from 'src/components/toast';
import { dayInTZ } from 'src/components/helpers';
import Swal from 'sweetalert2';

const CommentsSectionWrapper = styled.div`
  margin-top: 32px;
  padding: 0;

  .section-title {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 24px;
    color: #1a202c;
    display: flex;
    align-items: center;
    gap: 8px;

    &:before {
      content: '💬';
      font-size: 1.25rem;
    }
  }

  .loading-container {
    position: relative;
    min-height: 200px;
    background: #f7fafc;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .comments-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .comment-item {
    padding: 16px 20px;
    background: #f8f9fa;
    border-left: 4px solid;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;

    .comment-header {
      font-size: 0.95rem;
      font-weight: 600;
      color: #2d3748;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 8px;

      .user-avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        color: white;
        font-size: 0.8rem;
        flex-shrink: 0;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .user-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
    }

    .comment-text {
      margin: 0;
      margin-left: 40px;
      color: #2d3748;
      font-size: 0.95rem;
      line-height: 1.6;
      white-space: pre-wrap;
      word-break: break-word;
    }

    .comment-actions {
      .ui.button.basic {
        color: #e53e3e;
        border-color: #e53e3e;
        background: transparent;
        transition: all 0.2s ease;

        &:hover {
          background: #e53e3e;
          color: white;
          border-color: #e53e3e;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(229, 62, 62, 0.2);
        }

        &:active {
          transform: translateY(0);
          box-shadow: none;
        }
      }
    }
  }

  .empty-state {
    padding: 40px 20px;
    text-align: center;
    color: #718096;
    font-size: 0.95rem;
    background: #f7fafc;
    border-radius: 8px;
    border: 2px dashed #e2e8f0;
  }

  .comment-input-wrapper {
    margin-top: 24px;
    padding: 20px;
    background: #ffffff;
    border: 2px solid #e2e8f0;
    border-radius: 12px;

    textarea {
      width: 100%;
      min-height: 100px;
      padding: 12px;
      font-size: 0.95rem;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      resize: vertical;
      font-family: inherit;

      &::placeholder {
        color: #a0aec0;
      }
    }
  }

  .button-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
    gap: 8px;
  }
`;

// Generate a consistent color for each user based on their ID
const getUserColor = (userId) => {
  const colors = [
    '#e74c3c', // red
    '#3498db', // blue
    '#2ecc71', // green
    '#f39c12', // orange
    '#9b59b6', // purple
    '#1abc9c', // turquoise
    '#e67e22', // carrot
    '#34495e', // dark gray-blue
    '#16a085', // green sea
    '#c0392b', // dark red
    '#8e44ad', // wisteria
    '#27ae60', // nephritis
    '#f1c40f', // yellow
    '#d35400', // pumpkin
    '#2c3e50', // midnight blue
  ];

  // Use a simple hash function to convert userId to a number
  const hash = String(userId)
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);

  return colors[hash % colors.length];
};

// Get user initials for avatar
const getUserInitials = (name, lastName) => {
  const firstInitial = name?.charAt(0)?.toUpperCase() || '';
  const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
  return `${firstInitial}${lastInitial}`;
};

const CommentsSection = ({ lesson, guest }) => {
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAddComment, setLoadingComment] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [lesson?.id]);

  const handleAddComment = async () => {
    if (comment.trim()) {
      try {
        setLoadingComment(true);
        const response = await academyService.sendLessonComment({
          lesson_id: lesson.id,
          comment,
          guest_id: guest.id,
          guest_identification: guest.identification,
        });
        if (response?.data?.success) {
          Toast.fire({
            icon: 'success',
            title: 'Comentario agregado correctamente',
          });
          setComment('');
          fetchComments();
        }
      } catch (error) {
        console.error('Error adding comment:', error);
        Toast.fire({
          icon: 'error',
          title: 'Error al agregar comentario',
          text: error?.response?.data?.message || 'Hubo un problema al enviar el comentario',
        });
      } finally {
        setLoadingComment(false);
      }
    }
  };

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await academyService.getLessonComments({ lesson_id: lesson.id });
      setComments(response?.data?.comments || []);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    const result = await Swal.fire({
      title: '¿Estás seguro de querer eliminar este comentario?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
    });
    if (result.value) {
      try {
        setLoadingComment(true);
        const response = await academyService.deleteLessonComment({
          comment_id: commentId,
          guest_id: guest.id,
          guest_identification: guest.identification,
          lesson_id: lesson.id,
        });
        if (response?.data?.success) {
          fetchComments();
          Toast.fire({
            icon: 'success',
            title: 'Comentario eliminado correctamente',
          });
        }
      } catch (error) {
        console.error('Error deleting comment:', error);
        Toast.fire({
          icon: 'error',
          title: 'Error al eliminar comentario',
          text: error?.response?.data?.message || 'Hubo un problema al eliminar el comentario',
        });
      } finally {
        setLoadingComment(false);
      }
    }
  };

  const hasComments = comments.length > 0;

  return (
    <CommentsSectionWrapper>
      <h3 className="section-title">Comentarios</h3>

      {loading ? (
        <div className="loading-container">
          <Dimmer active inverted>
            <Loader size="medium">Cargando comentarios...</Loader>
          </Dimmer>
        </div>
      ) : (
        <ul className="comments-list">
          {hasComments ? (
            comments.map((commentItem, index) => {
              const userColor = getUserColor(commentItem.guest_id);
              const userInitials = getUserInitials(
                commentItem.Guest.name,
                commentItem.Guest.lastName
              );
              const hasPhoto = commentItem.Guest.photo;

              return (
                <li key={index} className="comment-item" style={{ borderLeftColor: userColor }}>
                  <div>
                    <div className="comment-header">
                      <div
                        className="user-avatar"
                        style={{ backgroundColor: hasPhoto ? 'transparent' : userColor }}
                      >
                        {hasPhoto ? (
                          <img src={commentItem.Guest.photo} alt={`${commentItem.Guest.name}`} />
                        ) : (
                          userInitials
                        )}
                      </div>
                      <div className="user-info">
                        <span>
                          {commentItem.Guest.name} {commentItem.Guest.lastName}
                        </span>
                        <span
                          style={{ fontSize: '0.75rem', color: '#6c757d', fontWeight: 'normal' }}
                        >
                          {dayInTZ(commentItem.createdAt, 'DD/MMM/YYYY HH:mm a')}
                        </span>
                      </div>
                    </div>
                    <p className="comment-text">{commentItem.comment}</p>
                  </div>
                  {commentItem.guest_id === guest.id && (
                    <div className="comment-actions">
                      <Button basic size="tiny" onClick={() => handleDeleteComment(commentItem.id)}>
                        Eliminar
                      </Button>
                    </div>
                  )}
                </li>
              );
            })
          ) : (
            <div className="empty-state">No hay comentarios aún. ¡Sé el primero en comentar!</div>
          )}
        </ul>
      )}

      <div className="comment-input-wrapper">
        <TextArea
          placeholder="Escribe tu comentario aquí..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
        />
        <div className="button-wrapper">
          {comment.trim() && (
            <Button
              basic
              loading={loadingAddComment}
              size="small"
              onClick={() => setComment('')}
              disabled={loadingAddComment}
            >
              Cancelar
            </Button>
          )}
          <Button
            primary
            loading={loadingAddComment}
            size="small"
            onClick={handleAddComment}
            disabled={!comment.trim() || loadingAddComment}
          >
            {loadingAddComment ? 'Enviando comentario...' : 'Agregar Comentario'}
          </Button>
        </div>
      </div>
    </CommentsSectionWrapper>
  );
};

export { CommentsSection };
